import type { ReagentDef, Recipe, RecipePayload } from '@ctcells/contract'
import { REAGENT_CHANNEL_MAX } from '@ctcells/fixtures'
import { t } from '@ctcells/i18n'

import { trayPathOf } from './trayPath.ts'

/**
 * 레시피 절 편집 상태 — 와이어프레임 `rcpState()` 2043행 · `rcpSummaryRows()` 2072행.
 *
 * 「새 레시피 › 값 직접 설정」과 「레시피 상세」가 **같은 카드**를 쓴다(`rcpCards` 2046행).
 * 그래서 상태 모양도 한 벌이다.
 *
 * 🔴 서버가 계산할 값을 여기서 만들지 않는다. 스텝 수는 레시피 payload 에서 **세어** 오고,
 *    절을 끄면 그 모듈 몫이 0 이 되는 것까지가 화면이 하는 전부다.
 */

/**
 * 채널 상한. 🔴 **종전 `REAGENT_WELL_SLOTS`(`RA1…RC3`)를 되살리지 마라**(2026-09-21) —
 *    그 자리 이름은 실물 응답에 «없다»(`ReagentDef` 주석의 112칸 전수 실측).
 */
export const REAGENT_CHANNEL_LIMIT = REAGENT_CHANNEL_MAX

export interface ReagentRow {
  channel: string
  /** 화면 표기 이름(시약) */
  name: string
  /** 성분 */
  component: string
  /**
   * 🔴 **`well: string` 을 되살리지 마라**(2026-09-21). `def.well ?? ''` 로 읽고 있었는데
   *    실물에 그 키가 «없어» **모든 행의 「웰」 열이 언제나 비어** 있었다.
   *    실물이 자리를 말하는 곳은 `labware_id` 와 `volumeByWell` 의 키 둘이다.
   *
   * 어느 랩웨어 자리인가 — `staining.reagent_bay.ambient.1` 꼴. 없으면 빈 문자열
   */
  labwareId: string
  /** 담긴 웰들. `volumeByWell` 의 키를 `use_order` 차례로 읽는다 */
  wells: string
  /** 총 담긴 용량(µL). `volumeByWell` 값의 합. 없으면 `null`(0 이 아니다 — 모른다) */
  volumeUl: number | null
  /** 작업 생성 시 lot 을 입력받는 채널인가 */
  perBatch: boolean
  lot: string
}

export interface RecipeFormState {
  centrifuge: boolean
  assembler: boolean
  staining: boolean
  reagents: ReagentRow[]
}

export interface StepCounts {
  centrifuge: number
  assembler: number
  staining: number
}

/**
 * 「단계」 칩 하나 — 이름 + 수. 🔴 수는 응답(`payload`)에서 센 값뿐이다.
 * ⚠ 모델은 여기 · 그리는 쪽(`StepCountChips`)은 `RecipeTables.tsx` — 컴포넌트 파일이 컴포넌트만 내보내야 핫리로드가 선다
 */
export interface StepChip {
  key: string
  label: string
  count: number
}

/** 옛 형식 레시피의 모듈별 스텝 수 → 칩 셋(원심 · 어셈블 · 염색 — 순서는 공정 순서) */
export function moduleStepChips(counts: StepCounts): StepChip[] {
  return [
    { key: 'centrifuge', label: t('section.centrifuge'), count: counts.centrifuge },
    { key: 'assembler', label: t('section.assembler'), count: counts.assembler },
    { key: 'staining', label: t('section.staining'), count: counts.staining },
  ]
}

/** 레시피 payload 에서 절 편집 상태를 만든다. 없는 절은 꺼진 상태로 시작한다 */
export function recipeFormFrom(recipe: Recipe): RecipeFormState {
  return recipeFormFromPayload(recipe.payload)
}

/**
 * 🔴 **`Recipe` 가 아니라 `payload` 를 받는 갈래**(신설 2026-09-21). 「JSON 가져오기」로
 *    방금 읽은 **문서**에는 `recipe_id`·`version` 이 없다 — 서버가 만든다. 그 문서의 절·시약을
 *    미리보기에 보이려면 본문만으로 세는 길이 있어야 한다.
 * ⚠ 이것이 없어서 새 레시피 화면이 **fixture 레시피**(`RECIPE_ICC_STANDARD`)로 요약을 만들고
 *   있었다 — 고른 것과 무관한 수가 「만들어질 레시피」에 떴다.
 */
/**
 * 시약이 담긴 웰 — `volumeByWell` 의 키를 `use_order` 차례로 읽는다.
 * 🔴 **모양을 확인하고 꺼낸다.** 자유 객체라 계약이 안을 보증하지 않는다.
 */
function reagentWells(def: ReagentDef): string[] {
  const byWell = def.volumeByWell
  if (byWell === undefined || byWell === null) return []
  const keys = Object.keys(byWell)
  const order = def.use_order
  if (order === undefined) return keys
  // ⚠ 차례에 없는 웰을 버리지 않는다 — 버리면 「담긴 곳이 없다」로 읽힌다
  return [...order.filter((w) => keys.includes(w)), ...keys.filter((w) => !order.includes(w))]
}

/** 총 용량(µL). 🔴 값이 없으면 `null` 이다 — `0` 은 「비었다」라서 다른 말이다 */
function reagentTotalVolume(def: ReagentDef): number | null {
  const byWell = def.volumeByWell
  if (byWell === undefined || byWell === null) return null
  const values = Object.values(byWell).filter((v) => typeof v === 'number' && Number.isFinite(v))
  return values.length === 0 ? null : values.reduce((sum, v) => sum + v, 0)
}

/**
 * 작업 생성 때 **lot 을 받는 채널** — `runtime_parameters[].binds_to` 가 말한다.
 *
 * 🔴 **화면이 «추론»하던 자리였다**(2026-09-21 수정). 종전 규칙은
 *    `def.lot === undefined && (def.storage_celsius ?? 22) < 10` — 「lot 칸이 비었고 차가우면
 *    작업마다 받는 채널」이라는 **짐작**이었고 실물에서 두 쪽으로 다 틀렸다:
 *    ⓐ 실물 `lot` 은 «없는» 게 아니라 **`""`(빈 문자열)** 이다 ⇒ `=== undefined` 가 거짓이라
 *       **모든 채널이 언제나 「작업마다 아님」**이었다(fixture 만 `lot` 키를 빼서 목에서는 맞아 보였다)
 *    ⓑ 온도는 그 사실과 무관하다 — 상온 채널도 lot 을 받을 수 있다
 * 🔴 실물이 그 사실을 **명시**한다: `runtime_parameters` 에
 *    `{name:'pAb_lot', binds_to:'reagents.5.lot', mandatory:true}` 가 있다 — 「5번 채널의 lot 을
 *    작업 생성 때 받는다」는 뜻이다. 짐작하지 말고 이것을 읽는다(DP-04 「화면은 상태를 추론하지 않는다」).
 * ⚠ 경로 모양이 `reagents.<채널>.lot` 이 아니면 **세지 않는다.** 다른 자리에 묶인 파라미터다.
 */
function lotBoundChannels(payload: RecipePayload): ReadonlySet<string> {
  const channels = new Set<string>()
  for (const param of payload.runtime_parameters ?? []) {
    const binds = param.binds_to
    if (typeof binds !== 'string') continue
    const match = /^reagents\.([^.]+)\.lot$/.exec(binds)
    if (match?.[1] !== undefined) channels.add(match[1])
  }
  return channels
}

export function recipeFormFromPayload(payload: RecipePayload): RecipeFormState {
  const lotBound = lotBoundChannels(payload)

  // ⚠ 본문이 **손으로 붙여 넣은 JSON** 일 수 있다 — 채널 값의 모양을 확인하고 읽는다
  const reagents = Object.entries(payload.reagents ?? {})
    .filter(([, def]) => typeof def === 'object' && def !== null)
    .map(([channel, def]) => ({
      channel,
      name: def.display_name ?? def.name ?? '',
      component: def.name ?? '',
      labwareId: def.labware_id ?? '',
      /*
       * 🔴 **웰은 `volumeByWell` 의 «키»다**(2026-09-21). 차례가 있으면(`use_order`)
       *    그 차례로 읽는다 — 어느 웰부터 빨아 쓰는지가 그 순서에 있다.
       */
      wells: reagentWells(def).join(' · '),
      volumeUl: reagentTotalVolume(def),
      perBatch: lotBound.has(channel),
      lot: def.lot ?? '',
    }))

  return {
    centrifuge: payload.centrifuge !== undefined,
    assembler: payload.assembler !== undefined,
    staining: payload.staining !== undefined,
    reagents,
  }
}

/** payload 의 스텝을 센다. 화면이 스텝 수를 지어내지 않는다 */
export function stepCountsOf(recipe: Recipe): StepCounts {
  return {
    centrifuge: recipe.payload.centrifuge?.steps?.length ?? 0,
    assembler: recipe.payload.assembler?.steps?.length ?? 0,
    staining: recipe.payload.staining?.steps.length ?? 0,
  }
}

export interface RecipeSummary {
  sections: string
  stepTotal: number
  reagents: string
  inputs: string
  /**
   * 위 두 문자열(절 · 작업 생성 입력)의 **조각**(` · ` 로 잇기 전). 상세의 요약 카드가 조각 사이에서만 꺾으려고 쓴다
   * (2026-09-29). 문자열과 같은 출처라 둘이 어긋나지 않는다.
   */
  sectionParts: string[]
  inputParts: string[]
  /** 모듈별 스텝 수(꺼진 절은 0) — 상세 요약 「스텝」 줄의 칩(`StepCountChips` · 2026-10-12)이 읽는다 */
  stepCounts: StepCounts
}

/** 요약 행(미리보기 · 상세 공용) — `rcpSummaryRows()` 2072행 */
export function recipeSummary(state: RecipeFormState, counts: StepCounts): RecipeSummary {
  const centrifuge = state.centrifuge ? counts.centrifuge : 0
  const assembler = state.assembler ? counts.assembler : 0
  const staining = state.staining ? counts.staining : 0

  const sectionNames = [
    state.centrifuge ? t('section.centrifuge') : '',
    state.assembler ? t('section.assembler') : '',
    state.staining ? t('section.staining') : '',
  ].filter((v) => v !== '')
  const sectionParts = sectionNames.length === 0 ? [t('state.empty.generic')] : sectionNames

  const perBatchNames = state.staining
    ? state.reagents.filter((r) => r.perBatch).map((r) => `${r.name} lot`)
    : []

  const inputParts = [t('column.sampleId'), ...perBatchNames]

  return {
    sections: sectionParts.join(' · '),
    stepTotal: centrifuge + assembler + staining,
    reagents: state.staining
      ? `${String(state.reagents.length)} ${t('unit.kinds')}`
      : t('mask.none'),
    inputs: inputParts.join(' · '),
    sectionParts,
    inputParts,
    stepCounts: { centrifuge, assembler, staining },
  }
}

/**
 * 트레이 순회 묶음에서 읽어내는 사실 — **환자 수 · Reference 웰 · 환자당 챔버**.
 *
 * 🔴 **한 벌이다.** 「JSON 가져오기」 판정(`parseRecipeJson`)과 레시피 절 카드
 *    (`RecipeSections` 의 「트레이 · 경로」)가 **같은 함수**를 쓴다. 종전에는 앞은 세고
 *    뒤는 `4` · `2` · `C3` 를 **리터럴로 박아** 어느 레시피를 열어도 같은 수가 떴다.
 *
 * 🔴 **`role` 이 없으면 `patient` 다** — `recipe.schema.json` `SampleGroup.role` 의
 *    `default: "patient"`. 종전 `parseRecipeJson` 은 `role === 'patient'` 로만 세어
 *    **역할을 안 적은 그룹을 환자에서 빠뜨렸다**(표준 레시피는 전건이 적어 안 드러났다).
 *
 * ⚠ 본문은 **손으로 붙여 넣은 JSON** 일 수도 있다(가져오기). 그래서 값을 믿지 않고
 *   모양을 확인한 뒤 읽는다 — 어긋나면 그 그룹을 세지 않는다.
 */
/**
 * 묶음 하나 — **레시피가 적은 그대로.** 🔴 값을 만들지 않는다(번호를 붙이지도, 채우지도 않는다).
 *
 * `role` 은 미기재를 `patient` 로 접어 «해석된» 값이고(스키마 `default`), `wells` 와
 * `sampleRef` 는 **원문 그대로**다. 모니터링의 환자별 챔버 그림이 이 배열의 **차례**를
 * 환자 번호로 읽는다 — 그래서 **순서를 재배열하지 않는다**.
 */
export interface SampleGroupShape {
  role: 'patient' | 'reference'
  /** 챔버(웰) 이름. 🔴 **레시피가 적은 이름 그대로** — `A1`·`C3`. 지어낸 번호를 붙이지 않는다 */
  wells: string[]
  /**
   * 🟢 레시피 안의 묶음 표지(`P1`·`REF` 실측). **시료 ID 가 아니다** —
   * 실물 시료(`FE-0921-H1-04`)와 잇는 축은 서버에 없다(BR-31).
   */
  sampleRef?: string
}

export interface SampleGroupFacts {
  /** 묶음 원문 — **순서 그대로.** 아래 셈들은 전부 이 배열에서 나온다 */
  groups: SampleGroupShape[]
  /** `role !== 'reference'` 인 묶음 수 */
  patients: number
  /** `role === 'reference'` 인 묶음 수 */
  reference: number
  /** Reference 묶음이 차지한 웰 — 순서 그대로 */
  referenceWells: string[]
  /** 환자 묶음의 웰 개수. **서로 다른 값만** 오름차순으로 담는다 — 갈리면 갈린 채로 둔다 */
  chambersPerPatient: number[]
}

interface RawGroup {
  role?: unknown
  wells?: unknown
  sample_ref?: unknown
}

/**
 * ⚠ `payload` 가 없어도(부가 조회의 목 · 옛 응답) 죽지 않는다 — 묶음 0 으로 돌려준다. 부르는 쪽이 부가 조회로
 *   쓸 때(작업 상세 시료 표의 웰 짝) 그 실패가 주 화면을 죽이면 안 된다(2026-09-30 실제로 죽었다).
 */
export function sampleGroupFacts(
  payload: Pick<RecipePayload, 'tray_path'> | undefined,
): SampleGroupFacts {
  // 옛 형식은 `tray_path` 그대로 · 새 형식(v2)은 트레이 시료 액체로 만든 같은 모양(`trayPathOf`)
  const raw: unknown = trayPathOf(payload)?.sample_groups
  const groups: RawGroup[] = Array.isArray(raw)
    ? raw.filter((g): g is RawGroup => typeof g === 'object' && g !== null)
    : []

  const wellsOf = (group: RawGroup): string[] =>
    Array.isArray(group.wells) ? group.wells.filter((w): w is string => typeof w === 'string') : []

  const isReference = (group: RawGroup) => group.role === 'reference'
  const patientGroups = groups.filter((g) => !isReference(g))
  const referenceGroups = groups.filter(isReference)

  const sizes = [...new Set(patientGroups.map((g) => wellsOf(g).length))]
    .filter((n) => n > 0)
    .sort((a, b) => a - b)

  const shapes: SampleGroupShape[] = groups.map((group) => ({
    role: isReference(group) ? 'reference' : 'patient',
    wells: wellsOf(group),
    /* 🔴 없으면 키를 만들지 않는다 — 「적히지 않았다」와 「빈 문자열」은 다른 사실이다 */
    ...(typeof group.sample_ref === 'string' && group.sample_ref !== ''
      ? { sampleRef: group.sample_ref }
      : {}),
  }))

  return {
    groups: shapes,
    patients: patientGroups.length,
    reference: referenceGroups.length,
    referenceWells: referenceGroups.flatMap(wellsOf),
    chambersPerPatient: sizes,
  }
}

/**
 * 원심 **회차** — `centrifuge.steps[].round` 가 말한다(스키마 「원심 회차 번호(1부터).
 * 같은 회차의 스텝들이 한 번에 돈다」).
 *
 * 🔴 **서로 다른 회차 번호를 오름차순으로 돌려준다.** 개수를 여기서 문장으로 만들지 않는다 —
 *    `1·2·3` 이면 「3」이라고 말할 수 있지만 `1·3` 이면 말할 수 없기 때문이다. 판단은 부르는 쪽.
 * ⚠ `centrifuge.steps` 는 **선택**이다(미지정이면 `ctcells_workflow.yaml` 절차를 따른다) —
 *   없으면 빈 배열이고, 화면은 그때 「모른다」로 그린다. **`3` 을 박지 마라**(종전 버그).
 */
export function centrifugeRounds(payload: Pick<RecipePayload, 'centrifuge'>): number[] {
  const steps = payload.centrifuge?.steps ?? []
  const rounds = steps
    .map((step) => step.round)
    .filter((round): round is number => typeof round === 'number')
  return [...new Set(rounds)].sort((a, b) => a - b)
}

export interface ParsedRecipeJson {
  /**
   * 🔴 **서버로 그대로 나가는 본문이다**(`RecipeWriteEnvelope.payload`).
   *    문서를 통째로 넘긴다 — `recipe_id`·`version`·`status`·`created_by`·`created_at` 다섯은
   *    **서버가 덮어쓴다**(`recipe_validate.compose` — 「요청 본문에 같은 키가 있어도 여기서 덮는다」).
   *    그래서 화면이 메타를 골라 지울 필요가 없다. 지우면 되레 갈래가 하나 더 생긴다.
   * ⚠ 구조 검증은 **서버가 한다**(`recipe.schema.json`, `additionalProperties: false`).
   *   여기 캐스팅은 「읽을 수 있는 객체다」까지이고 합격을 뜻하지 않는다.
   */
  payload: RecipePayload
  /** 문서 안의 이름. 화면 이름칸이 비면 이것이 선다 */
  name: string | undefined
  schemaVersion: string
  labware: number
  reagents: number
  centrifuge: number
  assembler: number
  staining: number
  wells: number
  patients: number
  reference: number
}

/**
 * 「JSON 가져오기」 본문 검사 — 와이어프레임 2018행.
 *
 * ⚠ 이것은 **입력 보조**다. 진짜 검증은 서버(`POST /recipes/{id}/validate`)가 한다.
 *   여기서 통과해도 서버가 거절할 수 있다.
 */
export function parseRecipeJson(text: string): ParsedRecipeJson | { error: string } {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { error: t('error.recipeJsonInvalid') }
  }

  if (typeof parsed !== 'object' || parsed === null)
    return { error: t('error.recipeJsonNotObject') }

  const root = parsed as Record<string, unknown>
  const body = (
    typeof root['payload'] === 'object' && root['payload'] !== null ? root['payload'] : root
  ) as Record<string, unknown>

  const required = ['labware', 'reagents', 'centrifuge', 'assembler', 'staining']
  const missing = required.filter((key) => !(key in body))
  if (missing.length > 0) {
    return { error: t('error.recipeJsonMissingSections', { sections: missing.join(', ') }) }
  }

  const steps = (key: string): number => {
    const section = body[key]
    if (typeof section !== 'object' || section === null) return 0
    const list = (section as Record<string, unknown>)['steps']
    return Array.isArray(list) ? list.length : 0
  }

  const payload = body as unknown as RecipePayload
  const trayPath = (
    typeof body['tray_path'] === 'object' && body['tray_path'] !== null ? body['tray_path'] : {}
  ) as Record<string, unknown>
  const order = Array.isArray(trayPath['order']) ? trayPath['order'] : []
  // 🔴 절 카드와 **같은 함수**로 센다. 두 벌 만들지 않는다
  const facts = sampleGroupFacts(payload)

  return {
    payload,
    name: typeof root['name'] === 'string' ? root['name'] : undefined,
    schemaVersion:
      typeof root['schema_version'] === 'string'
        ? root['schema_version']
        : typeof body['schema_version'] === 'string'
          ? body['schema_version']
          : 'rcp/1.0',
    labware: Array.isArray(body['labware']) ? body['labware'].length : 0,
    reagents:
      typeof body['reagents'] === 'object' && body['reagents'] !== null
        ? Object.keys(body['reagents']).length
        : 0,
    centrifuge: steps('centrifuge'),
    assembler: steps('assembler'),
    staining: steps('staining'),
    wells: order.length,
    patients: facts.patients,
    reference: facts.reference,
  }
}
